import { Box, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ColorModeContext, tokens } from "shared/theme";
import { 
  LightModeOutlined, 
  DarkModeOutlined, 
  PersonOutlined, 
  Menu as MenuIcon, 
  Logout,
  ArrowBack
} from "@mui/icons-material";
import SignOutDialog from "platform/auth/pages/SignOutDialog";
import NotificationBarDialog from "../../scenes/incident/NotificationBarDialog";
import { toast } from "react-hot-toast";
import { getUserIdFromSession } from "shared/utils/sessionProfileStore";


const Topbar = ({ setIsSidebarOpen }) => {
  const theme = useTheme();
  const isDesktopSidebar = useMediaQuery(theme.breakpoints.up("md"));
  const colorMode = useContext(ColorModeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const colors = tokens(theme.palette.mode);
  const [incidentCount, setIncidentCount] = useState(0);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const handleIncidentCountUpdate = (count) => setIncidentCount(count);
  
  const handleLogoutDialogOpen = () => setLogoutDialogOpen(true);
  const handleLogoutDialogClose = () => setLogoutDialogOpen(false);
  const handleLogoutConfirm = () => setLogoutDialogOpen(false);
  
  const handleNotificationDialogOpen = () => setNotificationDialogOpen(true);
  const handleNotificationDialogClose = () => setNotificationDialogOpen(false);
  
  const handleUserDetailsOpen = () => {
    const userId = getUserIdFromSession();
    if (userId) {
      navigate("/profileDetails");
    } else {
      toast.error("Sign in again — user id not found in session.");
      console.error("User ID not found in session cookie");
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleGoBack = () => {
    navigate(-1); // Navigate to previous page
  };

  // Check if we should show the back button (not on dashboard or main pages)
  const shouldShowBackButton = () => {
    const mainPages = ['/dashboard', '/home', '/login', '/', '/resetPassword', '/notFound', '/register'];
    return !mainPages.includes(location.pathname);
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" p={2} alignItems="center">
        <Box display="flex" alignItems="center" gap={1}>
          {/* Back Button - Show only on non-main pages */}
          {shouldShowBackButton() && (
            <IconButton
              onClick={handleGoBack}
              aria-label="Go back"
              sx={{
                backgroundColor: colors.primary[400],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.primary[500],
                },
                borderRadius: '8px',
                p: 1,
                mr: 1
              }}
            >
              <ArrowBack />
            </IconButton>
          )}
          
          {!isDesktopSidebar && (
            <IconButton
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
              sx={{
                backgroundColor: colors.primary[400],
                color: colors.grey[100],
                "&:hover": {
                  backgroundColor: colors.primary[500],
                },
                borderRadius: "8px",
                p: 1,
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
        
        <Box display="flex">
          <IconButton onClick={colorMode.toggleColorMode} aria-label="Toggle color mode">
            {theme.palette.mode === "dark" ? <DarkModeOutlined /> : <LightModeOutlined />}
          </IconButton>
          <IconButton onClick={handleUserDetailsOpen} aria-label="User profile">
            <PersonOutlined />
          </IconButton> 
          <IconButton onClick={handleLogoutDialogOpen} aria-label="Logout">
            <Logout />
          </IconButton>
        </Box>
      </Box>

      <SignOutDialog
        open={logoutDialogOpen}
        onClose={handleLogoutDialogClose}
        onConfirm={handleLogoutConfirm}
        setLogoutDialogOpen={setLogoutDialogOpen}
      />

      <NotificationBarDialog
        open={notificationDialogOpen}
        onClose={handleNotificationDialogClose}
        onIncidentCountUpdate={handleIncidentCountUpdate}
      />
    </>
  );
};

export default Topbar;